import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { useCallback, useEffect, useMemo, useState } from "react";

import { db } from "integrations/firebase/firestore";

import {
  signalSchema,
  signalSelectionSchema,
  type Signal,
  type SignalSelection,
} from "./schemas";
import {
  resolveSignals,
  sortSignals,
  type ResolvedSignal,
} from "./signal-utils";
import { useSubcollection, type ContentState } from "./use-event-content";

// No orderBy: see sortSignals for why ordering happens client-side.
const signalsQuery = (id: string) =>
  query(collection(db, "events", id, "signals"));

const selectionsQuery = (id: string) =>
  query(collection(db, "events", id, "signal_selections"));

/** The event's pre-loaded signal list, ordered by displayOrder. An empty list
 *  means the organiser has not enabled signals for this event. */
export function useSignals(eventId: string): ContentState<Signal> {
  const state = useSubcollection(eventId, signalsQuery, signalSchema);
  return useMemo(
    () =>
      state.status === "success"
        ? { ...state, data: sortSignals(state.data) }
        : state,
    [state],
  );
}

/** Every member's selection, for resolving badge signals on directory cards. */
export function useSignalSelections(
  eventId: string,
): ContentState<SignalSelection> {
  return useSubcollection(eventId, selectionsQuery, signalSelectionSchema);
}

/**
 * attendeeId → that member's picks resolved to live signals, in pick order
 * (first = badge). The one join used by every surface that shows someone
 * else's signals — directory cards, the attendee profile sheet, and the
 * speaker profile sheet via `speaker.attendeeId`. Empty while loading or on
 * error: a missing badge is the correct degraded rendering.
 */
export function useSignalsByAttendee(
  eventId: string,
): Map<string, ResolvedSignal[]> {
  const signals = useSignals(eventId);
  const selections = useSignalSelections(eventId);
  return useMemo(() => {
    if (signals.status !== "success" || selections.status !== "success")
      return new Map<string, ResolvedSignal[]>();
    return new Map(
      selections.data.map((selection) => [
        selection.id,
        resolveSignals(signals.data, selection.signalIds),
      ]),
    );
  }, [signals, selections]);
}

export type OwnSelectionState =
  | { status: "loading" }
  | { status: "error"; error: string }
  /** The member has no attendee profile, so the rules give them nothing to
   *  write to. Two ways in: an admin who never registered, and a member whose
   *  attendee row an admin deleted, which leaves members/{uid}.attendeeId
   *  pointing at a row that is gone. The read finds the second by its missing
   *  directory entry; if that read fails, it only shows up on the write. */
  | { status: "unavailable" }
  /** No selection document yet — this is what triggers the first-login prompt. */
  | { status: "not-picked" }
  /** A recorded answer, including the empty "skipped" one. */
  | { status: "picked"; signalIds: string[] };

/**
 * How a save ended. `failed` is worth a retry. `gone` is not: the rules refused
 * the write because the attendee row is missing, so no retry can land.
 */
export type SignalSaveResult = "saved" | "failed" | "gone";

/**
 * The signed-in member's own selection, plus the write.
 *
 * `save` performs the direct Firestore write the rules validate (own
 * attendeeId, at most three live signal ids, server timestamp) and reflects
 * the result into local state so the prompt closes without a refetch.
 */
export function useOwnSignalSelection(
  eventId: string,
  attendeeId: string | undefined,
): {
  state: OwnSelectionState;
  save: (signalIds: string[]) => Promise<SignalSaveResult>;
} {
  const [state, setState] = useState<OwnSelectionState>(
    attendeeId ? { status: "loading" } : { status: "unavailable" },
  );

  useEffect(() => {
    if (!attendeeId) {
      setState({ status: "unavailable" });
      return;
    }
    let cancelled = false;
    let stopWatching: (() => void) | undefined;
    setState({ status: "loading" });
    getDoc(doc(db, "events", eventId, "signal_selections", attendeeId))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          // No selection yet. Before asking for one, check the attendee
          // profile exists, or the prompt reopens on every visit and the save
          // is refused. events/{id}/attendees is readable by event admins
          // only, so this watches the directory entry that mirrors it: every
          // member may read it, whatever `configAttendees` says, and the
          // attendee deletion removes it with the row.
          //
          // It is a listener and not a single read because a new registrant
          // gets their member and attendee documents in one transaction and
          // the directory entry a moment later, from a trigger. A missing
          // entry therefore holds the prompt back, and the entry arriving
          // opens it. A failed read proves nothing, so it keeps the old
          // behaviour (the prompt, and the toast if the save is refused).
          const keep = (
            next: OwnSelectionState,
          ): ((prev: OwnSelectionState) => OwnSelectionState) => {
            return (prev) =>
              prev.status === "picked" || prev.status === "error" ? prev : next;
          };
          stopWatching = onSnapshot(
            doc(db, "events", eventId, "directory", attendeeId),
            (entry) => {
              if (cancelled) return;
              setState(
                keep(
                  entry.exists()
                    ? { status: "not-picked" }
                    : { status: "unavailable" },
                ),
              );
            },
            () => {
              if (cancelled) return;
              setState(keep({ status: "not-picked" }));
            },
          );
          return;
        }
        const parsed = signalSelectionSchema.safeParse({
          id: snap.id,
          ...snap.data(),
        });
        // A document that exists but fails to parse must NOT become
        // "not-picked": that would re-fire the first-login prompt and let a
        // Save/Skip silently overwrite whatever the unreadable doc recorded.
        setState(
          parsed.success
            ? { status: "picked", signalIds: parsed.data.signalIds }
            : { status: "error", error: "Recorded selection is unreadable" },
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          error: error instanceof Error ? error.message : "Failed to load",
        });
      });
    return () => {
      cancelled = true;
      stopWatching?.();
    };
  }, [eventId, attendeeId]);

  const save = useCallback(
    async (signalIds: string[]): Promise<SignalSaveResult> => {
      // The page never offers the picker without an attendee row; this is a guard.
      if (!attendeeId) return "gone";
      try {
        await setDoc(
          doc(db, "events", eventId, "signal_selections", attendeeId),
          { signalIds, updatedAt: serverTimestamp() },
        );
        setState({ status: "picked", signalIds });
        return "saved";
      } catch (error: unknown) {
        // The rule fences this write on the attendee row existing. A refusal
        // therefore usually means the row is gone, and nothing the member does will
        // make the write land: leaving the state at "not-picked" reopens the
        // prompt on every visit with Save and Skip both failing, and no way
        // out. "unavailable" is the same situation the no-attendeeId case
        // already describes, so say so and close the prompt.
        if (
          typeof error === "object" &&
          error !== null &&
          (error as { code?: unknown }).code === "permission-denied"
        ) {
          setState({ status: "unavailable" });
          return "gone";
        }
        return "failed";
      }
    },
    [eventId, attendeeId],
  );

  return { state, save };
}
